'use client'

import React from 'react'
import { useSelectedLayoutSegment } from 'next/navigation'
import { Loader2, Printer } from 'lucide-react'
import { toast } from 'sonner'
import { getKotHtml } from '@/lib/actions/print/get-kot-html'
import { useKotPrinter } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import { Badge, Button, Indicator, Separator, Thumbnail } from '@/components/ui'

interface KotItemProps extends React.ComponentPropsWithoutRef<'div'> {
  order?: any
  printReceipt: any
  requestPrinter: any
  printer: any
  printerType: any
}

export const KotItem = ({
  order,
  printReceipt,
  requestPrinter,
  printer,
  printerType,
  className,
  ...props
}: KotItemProps) => {
  const [isHtmlPrinting, startHtmlPrinting] = React.useTransition()
  const segment = useSelectedLayoutSegment()
  const { status_cart, id, reference } = order?.cart ?? {}
  const isActive = Number(segment) === id

  const printNow = async () => {
    if (printerType === 'html') {
      startHtmlPrinting(async () => {
        const html = await getKotHtml(order?.cart?.id)
        if (html?.url) {
          window.open(html?.url, '_blank')
        } else {
          toast.error('Failed to generate printable pdf')
        }
      })

      return
    }

    await printReceipt(order)
  }

  const isPartiallyFulfilled = order?.items?.some(
    (item: any) =>
      item?.status_fulfilment === 'partially_fulfilled' ||
      item?.status_fulfilment === 'fulfilled',
  )

  const badgeVariant = () => {
    if (isPartiallyFulfilled) return 'default'
    if (status_cart === 'ready') return 'warning'
    if (status_cart === 'completed') return 'green'
    return 'secondary'
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    requestPrinter()
  }, [])

  return (
    <div
      className={cn(
        'group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-lg border border-gray-100 bg-white text-left shadow-card transition-colors hover:border-gray-200',
        className,
      )}
      {...props}
    >
      <div
        className={cn('flex items-center gap-3 bg-gray-50/50 px-4 py-3', {
          'bg-gray-950 text-white': isActive,
        })}
      >
        <div className="flex w-full items-center gap-2">
          <h1 className="font-medium">#{id}</h1>
          <Indicator variant="neutral" className="size-1 bg-gray-200" />
          <span className="text-sm">
            {order?.cart_items_count} Item
            {order?.cart_items_count !== 1 && 's'}
          </span>
        </div>

        <div className="flex shrink-0 items-center">
          <Badge variant={badgeVariant()} size="lg" className="capitalize">
            {isPartiallyFulfilled ? 'Active' : status_cart}
          </Badge>
        </div>
      </div>

      <Separator className={cn({ 'opacity-0': isActive })} />

      <div className="flex h-full w-full flex-col gap-1.5 px-4 py-4">
        {order?.cart_items?.map((item: any, index: number) => {
          return (
            <div key={index} className="flex w-full items-center gap-2">
              <Thumbnail
                size="sm"
                className="size-8 shrink-0 border border-gray-100/60 bg-white"
                src={item.image_url}
              />
              <Badge variant="outline" className="h-auto text-sm font-normal">
                {item?.quantity}x
              </Badge>
              <span>{item?.product_name}</span>
            </div>
          )
        })}
      </div>

      <Separator className={cn({ 'opacity-0': isActive })} />

      <div className="flex w-full flex-grow-0 items-center justify-between gap-2 bg-gray-50/50 p-4 capitalize">
        <p className="text-gray-950">{reference}</p>

        <div className="flex">
          {printer ||
            (printerType === 'html' && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  printNow()
                }}
                disabled={isHtmlPrinting}
              >
                {isHtmlPrinting && <Loader2 className="size-4 animate-spin" />}
                {!isHtmlPrinting && <Printer className="size-4" />}

                {isHtmlPrinting ? 'Printing...' : 'Print'}
              </Button>
            ))}

          {!printer && printerType !== 'html' && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                requestPrinter()
              }}
            >
              <Printer className="size-4" />
              Connect Printer
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
